import { z } from "zod";
import { GbApiUserRights } from "../../enums/GbApiUserRightEnum";
import IGbContext from "../../interfaces/IGbContext";

// Rights rarely change, so a reload within this time reuses them instead of asking CoreAPI again
export const USER_RIGHTS_CACHE_TTL_MS = 60 * 60 * 1000;

const KEY_PREFIX = "gbApiUserRights";

const cachedRightsSchema = z.object({
    fetchedAt: z.number(),
    rights: z.record(z.string(), z.boolean())
});

// Rights are checked per project, and the token (so the user) can change with ?token=
export const userRightsCacheKey = (userId: string, gbContext: IGbContext): string =>
    `${KEY_PREFIX}:${userId}:${gbContext.organisationId}:${gbContext.tenantId}:${gbContext.projectId}`;

// Null when there is no fresh entry, or storage is unavailable or holds something unreadable.
// An entry that doesn't answer every right in requiredRights was cached before a right was added to the app, so it's stale too.
export const readCachedRights = (key: string, requiredRights: string[] = []): GbApiUserRights | null => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const parsed = cachedRightsSchema.safeParse(JSON.parse(raw));
        if (!parsed.success) return null;

        const { fetchedAt, rights } = parsed.data;
        const age = Date.now() - fetchedAt;
        const fresh = age >= 0 && age < USER_RIGHTS_CACHE_TTL_MS;
        return fresh && requiredRights.every(right => right in rights) ? rights : null;
    } catch {
        return null;
    }
};

export const writeCachedRights = (key: string, rights: GbApiUserRights): void => {
    try {
        localStorage.setItem(key, JSON.stringify({ fetchedAt: Date.now(), rights }));
    } catch {
        // Storage blocked or full: rights are just fetched again next time
    }
};
